"use client";

import {
  Action,
  Core,
  Expressive,
  Message,
} from "@faire/slate/foundation/color";

import Color from "../../slate/Color";
import {
  eventsThemeColors,
  getEventTheme,
} from "../../slate/Theme/eventsThemeColors";

import { ButtonVariantTheme, IButtonTheme } from "./types";

const REGULAR_BUTTON_HEIGHT = 48;
const SMALL_BUTTON_HEIGHT = 40;

const calculateButtonHeight = (small?: boolean) => {
  return `${small ? SMALL_BUTTON_HEIGHT : REGULAR_BUTTON_HEIGHT}px`;
};

export const primaryDestructiveStyle: ButtonVariantTheme = {
  base: {
    color: Message.text.critical,
    "background-color": Message.surface.critical,
    border: `1px solid ${Message.surface.critical}`,
  },
  loading: {
    color: Message.icon.critical,
    "background-color": Message.surface.critical,
    border: `1px solid ${Message.surface.critical}`,
  },
  hover: {
    color: Core.text.primaryInverse,
    "background-color": Color.siren,
    border: `1px solid ${Color.siren}`,
  },
  disabled: {
    color: Action.text.disabled,
    "background-color": Action.surface.disabled,
    border: `1px solid ${Action.border.disabled}`,
  },
  active: {
    color: Core.text.primaryInverse,
    "background-color": Color.siren,
    border: `1px solid ${Color.siren}`,
  },
  focus: {
    color: Message.text.critical,
  },
};

export const secondaryDestructiveStyle: ButtonVariantTheme = {
  base: {
    color: Message.text.critical,
    "background-color": Core.surface.primary,
    border: `1px solid ${Message.border.critical}`,
  },
  loading: {
    color: Message.icon.critical,
    "background-color": Core.surface.primary,
    border: `1px solid ${Message.border.critical}`,
  },
  hover: {
    color: Message.text.critical,
    "background-color": Message.surface.critical,
    border: `1px solid ${Message.border.critical}`,
  },
  disabled: {
    color: Action.text.disabled,
    "background-color": Core.surface.primary,
    border: `1px solid ${Action.border.disabled}`,
  },
  active: {
    color: Message.text.critical,
    "background-color": Message.surface.critical,
    border: `1px solid ${Message.border.critical}`,
  },
};

export const tertiaryDestructiveStyle: ButtonVariantTheme = {
  base: {
    color: Message.text.critical,
    "text-decoration-color": Message.text.critical,
  },
  loading: {
    color: Message.icon.critical,
  },
  hover: {
    color: Expressive.text.red,
    "text-decoration-color": Expressive.text.red,
  },
  disabled: {
    color: Action.text.disabled,
    "text-decoration-color": Action.text.disabled,
  },
  active: {
    color: Expressive.text.red,
    "text-decoration-color": Expressive.text.red,
  },
  focus: {
    color: Message.text.critical,
    "text-decoration-color": Message.text.critical,
  },
};

const primaryStyle: ButtonVariantTheme = {
  base: ({ $small, $destructive }) => ({
    height: calculateButtonHeight($small),
    padding: "0 23px", // 24px padding - 1px border
    border: `1px solid ${Action.border.default}`,
    "background-color": Core.surface.primaryInverse,
    color: Core.text.primaryInverse,
    ...($destructive ? primaryDestructiveStyle.base : {}),
  }),
  loading: ({ $destructive }) => ({
    "background-color": Action.surface.active,
    color: Core.text.primaryInverse,
    "border-color": Action.surface.active,
    ...($destructive ? primaryDestructiveStyle.loading : {}),
  }),
  hover: ({ $destructive }) => ({
    "background-color": Action.surface.hover,
    "border-color": Action.border.hover,
    color: Core.text.primaryInverse,
    ...($destructive ? primaryDestructiveStyle.hover : {}),
  }),
  disabled: ({ $destructive }) => ({
    "background-color": Action.surface.disabled,
    "border-color": Action.border.disabled,
    color: Action.text.disabled,
    ...($destructive ? primaryDestructiveStyle.disabled : {}),
  }),
  active: ({ $destructive }) => ({
    color: Core.text.primaryInverse,
    ...($destructive ? primaryDestructiveStyle.active : {}),
  }),
  focus: ({ $destructive }) => ({
    color: Core.text.primaryInverse,
    ...($destructive ? primaryDestructiveStyle.focus : {}),
  }),
};

const secondaryStyle: ButtonVariantTheme = {
  base: ({ $small, $destructive }) => ({
    height: calculateButtonHeight($small),
    padding: "0 23px", // 24px padding - 1px border
    border: `1px solid ${Action.border.default}`,
    "background-color": Core.surface.primary,
    color: Core.text.primary,
    ...($destructive ? secondaryDestructiveStyle.base : {}),
  }),
  loading: ({ $destructive }) => ({
    color: Core.text.primary,
    ...($destructive ? secondaryDestructiveStyle.loading : {}),
  }),
  hover: ({ $destructive }) => ({
    "background-color": Action.surface.hover,
    color: Core.text.primaryInverse,
    ...($destructive ? secondaryDestructiveStyle.hover : {}),
  }),
  disabled: ({ $destructive }) => ({
    color: Action.text.disabled,
    "background-color": Core.surface.primary,
    border: `1px solid ${Core.border.subdued}`,
    ...($destructive ? secondaryDestructiveStyle.disabled : {}),
  }),
  active: ({ $destructive }) => ({
    ...($destructive ? secondaryDestructiveStyle.active : {}),
  }),
};

const tertiaryStyle: ButtonVariantTheme = {
  base: ({ $destructive }) => ({
    display: "inline-flex",
    "background-color": "transparent",
    "text-decoration": "underline",
    "text-decoration-color": Core.text.primary,
    color: Core.text.primary,
    ...($destructive ? tertiaryDestructiveStyle.base : {}),
  }),
  loading: ({ $destructive }) => ({
    color: Core.icon.primary,
    ...($destructive ? tertiaryDestructiveStyle.loading : {}),
  }),
  hover: ({ $destructive }) => ({
    color: Action.text.hover,
    "text-decoration": "underline",
    "text-decoration-color": Action.text.hover,
    ...($destructive ? tertiaryDestructiveStyle.hover : {}),
  }),
  disabled: ({ $destructive }) => ({
    color: Action.text.disabled,
    "text-decoration-color": Action.text.disabled,
    ...($destructive ? tertiaryDestructiveStyle.disabled : {}),
  }),
  active: ({ $destructive }) => ({
    ...($destructive ? tertiaryDestructiveStyle.active : {}),
  }),
  focus: ({ $destructive }) => ({
    "text-decoration": "underline",
    "text-decoration-color": Core.text.primary,
    ...($destructive ? tertiaryDestructiveStyle.focus : {}),
  }),
};

export const buttonTheme: IButtonTheme = {
  primary: primaryStyle,
  secondary: secondaryStyle,
  tertiary: tertiaryStyle,
};

const primaryStyleEventsTheme: ButtonVariantTheme = {
  ...buttonTheme.primary,
  base: ({ $small, $destructive }) => ({
    height: calculateButtonHeight($small),
    padding: "0 23px", // 24px padding - 1px border
    "background-color": getEventTheme("faireMarket").primaryDark,
    color: getEventTheme("faireMarket").promoTextInverse,
    ...($destructive ? primaryDestructiveStyle.base : {}),
  }),
};

const secondaryStyleEventsTheme: ButtonVariantTheme = {
  ...buttonTheme.secondary,
  base: ({ $small, $destructive }) => ({
    height: calculateButtonHeight($small),
    padding: "0 23px", // 24px padding - 1px border
    border: "unset",
    color: getEventTheme("faireMarket").promoText,
    "background-color": Core.surface.primary,
    ...($destructive ? secondaryDestructiveStyle.base : {}),
  }),
};

const tertiaryStyleEventsTheme: ButtonVariantTheme = {
  base: ({ $destructive }) => ({
    display: "inline-flex",
    "background-color": "transparent",
    "text-decoration": "underline",
    "text-decoration-color": eventsThemeColors.faireMarket.primaryDark,
    color: eventsThemeColors.faireMarket.primaryDark,
    ...($destructive ? tertiaryDestructiveStyle.base : {}),
  }),
  loading: ({ $destructive }) => ({
    color: Core.text.primaryInverse,
    ...($destructive ? tertiaryDestructiveStyle.loading : {}),
  }),
  hover: ({ $destructive }) => ({
    color: "#5262C7", // TODO: rename
    "text-decoration": "underline",
    "text-decoration-color": "#5262C7", // TODO: rename
    ...($destructive ? tertiaryDestructiveStyle.hover : {}),
  }),
  disabled: ({ $destructive }) => ({
    color: Action.text.disabled,
    "text-decoration-color": Action.surface.disabled,
    ...($destructive ? tertiaryDestructiveStyle.disabled : {}),
  }),
  active: ({ $destructive }) => ({
    color: eventsThemeColors.faireMarket.primaryDark,
    "text-decoration-color": eventsThemeColors.faireMarket.primaryDark,
    ...($destructive ? tertiaryDestructiveStyle.active : {}),
  }),
  focus: ({ $destructive }) => ({
    "text-decoration": "underline",
    "text-decoration-color": eventsThemeColors.faireMarket.primaryDark,
    ...($destructive ? tertiaryDestructiveStyle.focus : {}),
  }),
};

export const buttonEventsTheme: IButtonTheme = {
  primary: primaryStyleEventsTheme,
  secondary: secondaryStyleEventsTheme,
  tertiary: tertiaryStyleEventsTheme,
};
